import * as m from "mithril";
import { MoneyDTO, SeatSelectionDTO } from "../../apiclient/ApiDtos";
import { MessageProvider } from "../../context/AppMessages";
import { formatMoney } from "../../helpers/MoneyFormatter";
import CustomSvgIcon from "../../components/CustomSvgIcon";

// ------------------------------------------------------------
// Selected Seat Label Component
// ------------------------------------------------------------

interface SelectedSeatComponentAttrs {
  seat: SeatSelectionDTO;
}

export function SelectedSeatComponent(): m.Component<SelectedSeatComponentAttrs> {
  return {
    view(vnode) {
      return m(
        "span.seat-selection-slot.en-only.mr-2",
        vnode.attrs.seat.number == null || vnode.attrs.seat.number.trim() == "" ? " - " : vnode.attrs.seat.number
      );
    }
  };
}

// ------------------------------------------------------------
// Ordered Seats (Expandable / Collapsible Section)
// ------------------------------------------------------------

interface OrderedSeatsComponentAttrs {
  allowCollapse?: boolean;
  defaultExpanded?: boolean;
  messageProvider: MessageProvider;
  seatSelections: SeatSelectionDTO[];
  isSeatingAuto: boolean
}

export function OrderedSeatsComponent(): m.Component<OrderedSeatsComponentAttrs> {
  let isExpanded = false;

  return {
    oninit(vnode) {
      // defaultExpanded is only meaningful when allowCollapse=true
      isExpanded = vnode.attrs.allowCollapse
        ? !!vnode.attrs.defaultExpanded
        : true; // non-collapsible → always expanded
    },

    view(vnode) {
      const { allowCollapse, messageProvider, seatSelections } = vnode.attrs;

      const totalAmount = seatSelections.reduce(
        (acc, seat) => acc + seat.price.amount,
        0
      );

      const totalSeatPrice: MoneyDTO = {
        amount: totalAmount,
        currency: "MMK"
      };

      const isCollapsible = allowCollapse === true && seatSelections.length > 1;
      const showExpanded = !isCollapsible || (isCollapsible && isExpanded);

      return m("div", {
        onclick: () => (isExpanded = !isExpanded)
      },
        vnode.attrs.isSeatingAuto && m("div.important-info-alert mb-3 small font-weight-bold",
          vnode.attrs.messageProvider.get("small_car_details.seating_is_auto")
        ),
        m(".d-flex.justify-content-between",
          m(".text-muted mb-1 small font-weight-bold", messageProvider.get("seat_numbers")),

          isCollapsible &&
          m("div",
            m(CustomSvgIcon, {
              iconType: isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down",
              themeCssClass: "svg-info svg-medium"
            })
          )
        ),

        vnode.attrs.isSeatingAuto ?
          [
            m("div.d-flex small mt-3 font-weight-bold",
              m("div.text-muted", vnode.attrs.messageProvider.get("no_of_seats"), " -"),
              m("div.ml-3 ", vnode.attrs.seatSelections.length,
                " ",
                vnode.attrs.seatSelections.length > 1 ?
                  vnode.attrs.messageProvider.get("seats") :
                  vnode.attrs.messageProvider.get("seat")

              )
            ),
          ]

          :

          [
            // ---------------------- Expanded View: Seat List with Prices ----------------------
            showExpanded
              ? seatSelections.map(seat =>
                m(".row.my-2",
                  m(".col-6",
                    m(SelectedSeatComponent, { seat })
                  ),
                  m(".col-6.text-right",
                    m("span.text-dark", formatMoney(seat.price))
                  )
                )
              )

              // ---------------------- Collapsed View: Tiny Seat Badge Row ----------------------
              : m(".d-flex",
                seatSelections.map(seat =>
                  m(SelectedSeatComponent, { seat })
                )
              )
          ],

        // ---------------------- Total Price (Only in Expanded Mode) ----------------------
        //showExpanded &&
        seatSelections.length > 1 && m(".row.mt-3",
          m(".col-6",
            m("span.mr-2 small font-weight-bold", messageProvider.get("total_seat_price"))
          ),
          m(".col-6.text-right",
            m("span.text-success.font-weight-bold",
              seatSelections.length > 0 ? formatMoney(totalSeatPrice) : null
            )
          )
        )
      );
    }
  };
}
